import Lenis from "@studio-freight/lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import Swiper from "swiper";
import { Navigation, Pagination, Thumbs } from "swiper/modules";
import { Fancybox } from "@fancyapps/ui";
import Accordion from "accordion-js";
import SimpleScrollbar from "simple-scrollbar";

/*
This helper function makes a group of elements animate along the x-axis in a seamless, responsive loop.

Features:
 - Uses xPercent so that even if the widths change (like if the window gets resized), it should still work in most cases.
 - When each item animates to the left or right enough, it will loop back to the other side
 - Optionally pass in a config object with values like "speed" (default: 1, which travels at roughly 100 pixels per second), paused (boolean),  repeat, reversed, and paddingRight.
 - The returned timeline will have the following methods added to it:
	 - next() - animates to the next element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
	 - previous() - animates to the previous element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
	 - toIndex() - pass in a zero-based index value of the element that it should animate to, and optionally pass in a vars object to control duration, easing, etc. Always goes in the shortest direction
	 - current() - returns the current index (if an animation is in-progress, it reflects the final index)
	 - times - an Array of the times on the timeline where each element hits the "starting" spot. There's also a label added accordingly, so "label1" is when the 2nd element reaches the start.
 */
function horizontalLoop(items, config) {
	items = gsap.utils.toArray(items);
	config = config || {};
	let tl = gsap.timeline({
			repeat: config.repeat,
			paused: config.paused,
			defaults: { ease: "none" },
			onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100),
		}),
		length = items.length,
		startX = items[0].offsetLeft,
		times = [],
		widths = [],
		xPercents = [],
		curIndex = 0,
		pixelsPerSecond = (config.speed || 1) * 100,
		snap = config.snap === false ? (v) => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
		totalWidth,
		curX,
		distanceToStart,
		distanceToLoop,
		item,
		i;
	gsap.set(items, {
		// convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
		xPercent: (i, el) => {
			let w = (widths[i] = parseFloat(gsap.getProperty(el, "width", "px")));
			xPercents[i] = snap(
				(parseFloat(gsap.getProperty(el, "x", "px")) / w) * 100 +
					gsap.getProperty(el, "xPercent")
			);
			return xPercents[i];
		},
	});
	gsap.set(items, { x: 0 });
	totalWidth =
		items[length - 1].offsetLeft +
		(xPercents[length - 1] / 100) * widths[length - 1] -
		startX +
		items[length - 1].offsetWidth *
			gsap.getProperty(items[length - 1], "scaleX") +
		(parseFloat(config.paddingRight) || 0);
	for (i = 0; i < length; i++) {
		item = items[i];
		curX = (xPercents[i] / 100) * widths[i];
		distanceToStart = item.offsetLeft + curX - startX;
		distanceToLoop =
			distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
		tl.to(
			item,
			{
				xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
				duration: distanceToLoop / pixelsPerSecond,
			},
			0
		)
			.fromTo(
				item,
				{
					xPercent: snap(
						((curX - distanceToLoop + totalWidth) / widths[i]) * 100
					),
				},
				{
					xPercent: xPercents[i],
					duration:
						(curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
					immediateRender: false,
				},
				distanceToLoop / pixelsPerSecond
			)
			.add("label" + i, distanceToStart / pixelsPerSecond);
		times[i] = distanceToStart / pixelsPerSecond;
	}
	function toIndex(index, vars) {
		vars = vars || {};
		Math.abs(index - curIndex) > length / 2 &&
			(index += index > curIndex ? -length : length); // always go in the shortest direction
		let newIndex = gsap.utils.wrap(0, length, index),
			time = times[newIndex];
		if (time > tl.time() !== index > curIndex) {
			// if we're wrapping the timeline's playhead, make the proper adjustments
			vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
			time += tl.duration() * (index > curIndex ? 1 : -1);
		}
		curIndex = newIndex;
		vars.overwrite = true;
		return tl.tweenTo(time, vars);
	}
	tl.next = (vars) => toIndex(curIndex + 1, vars);
	tl.previous = (vars) => toIndex(curIndex - 1, vars);
	tl.current = () => curIndex;
	tl.toIndex = (index, vars) => toIndex(index, vars);
	tl.times = times;
	tl.progress(1, true).progress(0, true); // pre-render for performance
	if (config.reversed) {
		tl.vars.onReverseComplete();
		tl.reverse();
	}
	return tl;
}

(function ($, root, undefined) {
	$(function () {
		"use strict";

		console.log(
			"%cCreated by Konfiture",
			"color: #000; padding: 5px 0px 1px; border-bottom:2px solid #1A1A1A;"
		),
			console.log("%chttps://konfiture.com", "color:#ccc");

		gsap.registerPlugin(ScrollTrigger, SplitText);

		gsap.config({
			force3D: true,
		});

		gsap.defaults({
			ease: "expo.out",
			duration: 0.8,
		});

		/**
		 * Lenis init
		 */
		const lenis = new Lenis();

		lenis.on("scroll", ScrollTrigger.update);

		gsap.ticker.add((time) => {
			lenis.raf(time * 1000);
		});

		gsap.ticker.lagSmoothing(0);

		// First we get the viewport height and we multiple it by 1% to get a value for a vh unit
		let vh = window.innerHeight * 0.01;
		// Then we set the value in the --vh custom property to the root of the document
		document.documentElement.style.setProperty("--vh", `${vh}px`);

		// We listen to the resize event
		window.addEventListener("resize", () => {
			// We execute the same script as before
			let vh = window.innerHeight * 0.01;
			document.documentElement.style.setProperty("--vh", `${vh}px`);
		});

		/**
		 * Loader timeline
		 *
		 */
		const loaderTl = gsap.timeline({ paused: false, repeat: -1 });
		loaderTl
			.to(".loader img", {
				yPercent: -50,
				ease: "elastic.out(0.5, 0.3)",
				duration: 0.6,
				stagger: 0.2,
			})
			.to(".loader img", {
				yPercent: 0,
				ease: "elastic.out(0.5, 0.3)",
				duration: 0.6,
				stagger: 0.2,
			});

		/**
		 * General
		 *
		 */
		function generalScripts() {
			$('a[href*="#"]')
				// Remove links that don't actually link to anything
				.not('[href="#"]')
				.not('[href="#0"]')
				.on("click", function (event) {
					// On-page links
					if (
						location.pathname.replace(/^\//, "") ==
							this.pathname.replace(/^\//, "") &&
						location.hostname == this.hostname
					) {
						// Figure out element to scroll to
						var target = $(this.hash);
						target = target.length
							? target
							: $("[name=" + this.hash.slice(1) + "]");
						// Does a scroll target exist?
						if (target.length) {
							console.log($(target));
							// Only prevent default if animation is actually gonna happen
							event.preventDefault();
							lenis.scrollTo(target.offset().top, {
								offset: -document.querySelector("#masthead").offsetHeight,
								duration: 2,
								onComplete: () => {
									// Callback after animation
									// Must change focus!
									var $target = $(target);
									$target.focus();
									if ($target.is(":focus")) {
										// Checking if the target was focused
										return false;
									} else {
										$target.attr("tabindex", "-1"); // Adding tabindex for elements not focusable
										$target.focus(); // Set focus again
									}
								},
							});
						}
					}
				});

			// Mobile menu toggler
			$(".menu-toggle").on("click", function () {
				$("body").toggleClass("menu-open");

				if ($("body").hasClass("menu-open")) {
					lenis.stop();
				} else {
					lenis.start();
				}
			});

			// Minify navbar on scroll
			var $topbannerHeight = $(".site-header__top").outerHeight(),
				$defaultPadding = $(".site-header__menus").css("padding");

			ScrollTrigger.create({
				trigger: "body",
				start: "top -" + $topbannerHeight + "px",
				// markers: true,
				onEnter: function () {
					$(".site-header").toggleClass("--scrolled");

					gsap.to($(".site-header"), {
						y: -$topbannerHeight + "px",
					});

					if (window.innerWidth > 992) {
						gsap.to($(".site-header__menus"), {
							padding: "1rem 0",
						});
					}
				},
				onLeaveBack: function () {
					$(".site-header").toggleClass("--scrolled");

					gsap.to($(".site-header"), {
						y: 0,
						duration: 0.8,
						ease: "expo.out",
					});

					if (window.innerWidth > 992) {
						gsap.to($(".site-header__menus"), {
							padding: $defaultPadding,
							duration: 0.8,
							ease: "expo.out",
						});
					}
				},
			});

			// Parallax effects on scroll
			let initSpd = 50;

			if (window.innerWidth < 992) {
				initSpd = initSpd / 2;
			}

			$("[data-speed]").each(function (index) {
				var parallaxTl = gsap.timeline({
					scrollTrigger: {
						trigger: $(this),
						start: "top 100%",
						end: "bottom 0%",
						scrub: true,
						// markers: true,
					},
				});

				gsap.set($(this), {
					y: $(this).data("speed") * initSpd,
				});

				parallaxTl.to($(this), {
					y: -$(this).data("speed") * initSpd,
					scale: 1,
					ease: "none",
				});
			});

			// Default reveal
			$(".reveal").each(function () {
				gsap.set($(this).children(), {
					autoAlpha: 0,
					y: "2rem",
				});

				var defaultRevealTl = gsap.timeline({
					scrollTrigger: {
						trigger: this,
						start: "top 60%",
						// markers: true,
						toggleActions: "play none none none",
					},
				});

				defaultRevealTl.to($(this).children(), {
					autoAlpha: 1,
					y: 0,
					duration: 0.8,
					stagger: 0.1,
					ease: "expo.out",
				});
			});

			// Init tickers
			gsap.utils.toArray(".ticker").forEach((line, i) => {
				const els = line.querySelectorAll(".ticker > *"),
					tl = horizontalLoop(els, {
						repeat: -1,
						speed: 1,
					});
			});

			// .section-header reveal
			// $(".section-header").each(function () {
			// 	var icon = $(this).find(".icon figure img"),
			// 		iconbar = $(this).find(".icon span"),
			// 		text = $(this).find("h2"),
			// 		sectionReveal = gsap.timeline({
			// 			scrollTrigger: {
			// 				trigger: this,
			// 				start: "top 60%",
			// 				toggleActions: "play none none none",
			// 				// markers: true,
			// 			},
			// 		});

			// 	sectionReveal
			// 		.fromTo(
			// 			icon,
			// 			{
			// 				autoAlpha: 0,
			// 				scale: 0.6,
			// 			},
			// 			{
			// 				autoAlpha: 1,
			// 				scale: 1,
			// 				ease: "expo.out",
			// 				duration: 0.8,
			// 			}
			// 		)
			// 		.to(
			// 			iconbar,
			// 			{
			// 				autoAlpha: 1,
			// 				scaleX: 1,
			// 				ease: "expo.out",
			// 				duration: 0.8,
			// 			},
			// 			">-.8"
			// 		)
			// 		.fromTo(
			// 			text.find("span"),
			// 			{
			// 				autoAlpha: 0,
			// 				y: "2rem",
			// 			},
			// 			{
			// 				autoAlpha: 1,
			// 				y: 0,
			// 				ease: "expo.out",
			// 				duration: 0.8,
			// 				stagger: 0.2,
			// 			},
			// 			">-.8"
			// 		);
			// });

			/**
			 * Products slider
			 *
			 * swiper.js
			 *
			 **/

			if ($(".swiper.products-list").length > 0) {
				// Slider init
				var featuredProducts = new Swiper(".swiper.products-list", {
					modules: [Navigation],
					slidesPerView: 2,
					slidesPerGroup: 2,
					spaceBetween: 16,
					grabCursor: true,
					navigation: {
						nextEl: ".swiper-button-next",
						prevEl: ".swiper-button-prev",
					},
					breakpoints: {
						768: {
							slidesPerView: 3,
							spaceBetween: 24,
						},
						992: {
							slidesPerView: 4,
						},
					},
				});

				// Products-list reveal
				// var productsRevealTl = gsap.timeline({
				// 	scrollTrigger: {
				// 		trigger: ".products-list",
				// 		start: "top 60%",
				// 		// markers: true,
				// 		toggleActions: "play none none none",
				// 	},
				// });

				// productsRevealTl.fromTo(
				// 	$(".products-list .product"),
				// 	{
				// 		y: 40,
				// 		autoAlpha: 0,
				// 	},
				// 	{
				// 		y: 0,
				// 		autoAlpha: 1,
				// 		ease: "power3.inOut",
				// 		duration: 0.75,
				// 		stagger: 0.1,
				// 	}
				// );
			}

			/**
			 * Categories slider
			 *
			 * swiper.js
			 *
			 **/

			if ($(".categories-list").length > 0) {
				// Slider init
				var categoriesSw = new Swiper(".categories-list", {
					modules: [Navigation],
					slidesPerView: 1.5,
					spaceBetween: 16,
					grabCursor: true,
					navigation: {
						nextEl: ".swiper-button-next",
						prevEl: ".swiper-button-prev",
					},
					breakpoints: {
						992: {
							slidesPerView: 3,
							spaceBetween: 24,
						},
						1200: {
							slidesPerView: 4,
						},
					},
				});
			}

			/**
			 * Hide custom button on iframe playing
			 *
			 **/

			if ($(".iframe-wrapper").length > 0) {
				$(".iframe__btn").on("click", function () {
					var iframe = $(this).siblings("iframe");
					iframe[0].style.pointerEvents = "auto";

					//as noted in addendum, check for querystring exitence
					var symbol = iframe[0].src.indexOf("?") > -1 ? "&" : "?";
					//modify source to autoplay and start video
					iframe[0].src += symbol + "autoplay=1";

					gsap.to($(this), {
						autoAlpha: 0,
						duration: 0.5,
						ease: "expo.out",
					});
				});
			}
		}

		/**
		 * Page loaded animation
		 *
		 */
		function pageLoadedAnim() {
			// gsap.set($('.site-header'), {
			//   y: '-100%',
			// });

			gsap.set($(".mpp__main"), {
				// y: 100,
				autoAlpha: 0,
			});

			var loadedAnimation = gsap.timeline();

			loadedAnimation.to($(".mpp__main"), {
				y: 0,
				autoAlpha: 1,
				ease: "expo.out",
				duration: 1,
			});

			// loadedAnimation.to($('.site-header'), {
			//   y: '0%',
			//   duration: 1.2,
			//   ease: 'expo.out',
			//   onComplete: function () { ScrollTrigger.refresh() },
			// });
		}

		/**
		 * Review Slider init
		 * @require swiperjs
		 */
		function reviewSlider() {
			var swSpaceBetween = 96;
			if ($("body").hasClass("single-product")) {
				swSpaceBetween = 30;
			}

			var reviewSw = new Swiper(".mpp__reviews .swiper", {
				modules: [Navigation],
				slidesPerView: 1,
				spaceBetween: swSpaceBetween,
				centeredSlides: true,
				navigation: {
					prevEl: ".swiper-button-prev",
					nextEl: ".swiper-button-next",
				},
				breakpoints: {
					1200: {
						slidesPerView: 2,
						centeredSlides: false,
					},
				},
			});
		}

		/**
		 * Home
		 *
		 */
		function homeScripts() {
			/**
			 * Home vision animations
			 *
			 * gsap.js
			 *
			 **/
			const visionEls = $(".animated-icons div img");
			const visionTl = gsap.timeline({
				scrollTrigger: {
					trigger: ".home__vision",
					start: "top 50%",
					// markers: true,
					id: "icons",
					toggleActions: "play none none reverse",
				},
			});

			gsap.set(visionEls, {
				yPercent: 150,
			});

			visionEls.each(function (index) {
				visionTl.to(
					$(this),
					{
						yPercent: 0,
						duration: 1,
						ease: "elastic.out(0.5, 0.3)",
					},
					"<0.1"
				);
			});
		}

		/**
		 * Archive
		 *
		 */
		function archiveProductScripts() {
			if ($(window).width() > 992) {
				ScrollTrigger.create({
					trigger: ".mpp__products",
					start: "top " + $(".site-header__content").outerHeight() + "px",
					end: "bottom 50%",
					pin: ".mpp__products__prelist",
					pinSpacer: false,
					pinSpacing: false,
					id: "prelist",
					// markers: true,
				});
			} else {
				const hideControls = gsap.timeline({
					scrollTrigger: {
						trigger: "footer",
						start: "top 80%",
						end: "bottom 100%",
						id: "controls",
						// markers: true,
						toggleActions: "play none none reverse",
					},
				});

				hideControls.to(".mpp__products__prelist__controls", {
					yPercent: 100,
					duration: 0.5,
					ease: "ease.out",
				});
			}
		}

		/**
		 * Single
		 *
		 */
		function singleProductScripts() {
			const productDetails = new Accordion(".mpp__product__details", {
				elementClass: "mpp__product__details__item",
				panelClass: "mpp__product__details__item__panel",
				triggerClass: "mpp__product__details__item__trigger",
			});

			Fancybox.bind('[data-fancybox="gallery"]', {
				Toolbar: {
					display: [
						"zoom",
						// 'slideshow',
						// 'fullscreen',
						// 'download',
						// 'thumbs',
						"close",
					],
				},
			});

			if ($(window).width() > 992) {
				let endTrigger;

				endTrigger =
					"bottom " +
					($(window).height() - $(".site-header").outerHeight()) +
					"px";

				if ($(window).height() <= 800) {
					endTrigger = "bottom 100%";
				}

				// Fix product gallery on scroll
				ScrollTrigger.create({
					trigger: "#gallery-pin",
					start: "top " + $(".site-header").outerHeight() + "px",
					end: endTrigger,
					// markers: true,
					pin: ".mpp__product__gallery",
					pinSpacer: false,
					pinSpacing: false,
				});
			}

			const relatedSwiper = new Swiper(".up-sells .swiper", {
				modules: [Navigation, Pagination],
				slidesPerView: 2,
				slidesPerGroup: 2,
				spaceBetween: 16,
				pagination: {
					el: ".swiper-pagination",
					clickable: true,
				},
				navigation: {
					nextEl: ".swiper-button-next",
					prevEl: ".swiper-button-prev",
				},
			});

			const thumbsSwiper = new Swiper(".product-thumbs", {
				slidesPerView: "auto",
				spaceBetween: 16,
			});

			const productSwiper = new Swiper(".product-images", {
				modules: [Thumbs],
				thumbs: {
					swiper: thumbsSwiper,
				},
				navigation: {
					prevEl: ".swiper-button-prev",
					nextEl: ".swiper-button-next",
				},
				breakpoints: {
					768: {
						allowTouchMove: true,
					},
				},
			});

			// $.fancybox.defaults.backFocus = false;

			var $el, $ps, $up, $content, totalHeight;

			if ($(".mpp__product__more__guides__content").outerHeight() < 200) {
				$(".mpp__product__more__guides__content .read-more").addClass("hidden");
			}

			$(".read-more").click(function (e) {
				e.preventDefault();

				totalHeight = 0;

				$up = $(this).parent();
				$content = $up.find(".read-more-content");
				$content.addClass("expanded");
				$ps = $content.children();

				// measure how tall inside should be by adding together heights of all inside paragraphs (except read-more paragraph)
				$ps.each(function () {
					totalHeight += $(this).outerHeight(true);
				});

				$content
					.css({
						// Set height to prevent instant jumpdown when max height is removed
						height: $content.height(),
						"max-height": 9999,
					})
					.animate({
						height: totalHeight,
					});

				// fade out read-more
				$(this).addClass("hidden");
				// $(this).fadeOut();

				// prevent jump-down
				return false;
			});

			// AJAX add to cart
			$(document.body).on("added_to_cart", function () {
				setTimeout(function () {
					$(".added").removeClass("added");
				}, 2000);
			});

			$("form.cart").on("submit", function (e) {
				e.preventDefault();

				var form = $(this);
				form.addClass("--loading");
				// form.block({ message: null, overlayCSS: { background: '#fff', opacity: 0 } });

				var formData = new FormData(form[0]);
				formData.append("add-to-cart", form.find("[name=add-to-cart]").val());

				// Ajax action.
				$.ajax({
					url: wc_add_to_cart_params.wc_ajax_url
						.toString()
						.replace("%%endpoint%%", "ace_add_to_cart"),
					data: formData,
					type: "POST",
					processData: false,
					contentType: false,
					beforeSend: function () {
						$(".single_add_to_cart_button").addClass("--loading");
					},
					complete: function (response, status) {
						response = response.responseJSON;

						$(".single_add_to_cart_button").removeClass("--loading");

						if (!response) {
							return;
						}

						if (response.error && response.product_url) {
							window.location = response.product_url;
							return;
						}

						// Redirect to cart option
						if (wc_add_to_cart_params.cart_redirect_after_add === "yes") {
							window.location = wc_add_to_cart_params.cart_url;
							return;
						}

						var $thisbutton = form.find(".single_add_to_cart_button"); //
						//						var $thisbutton = null; // uncomment this if you don't want the 'View cart' button

						// Trigger event so themes can refresh other areas.
						$(document.body).trigger("added_to_cart", [
							response.fragments,
							response.cart_hash,
							$thisbutton,
						]);

						// Remove existing notices
						$(
							".woocommerce-error, .woocommerce-message, .woocommerce-info"
						).remove();

						// Add new notices

						if (
							$(response.fragments.notices_html).hasClass("woocommerce-error")
						) {
							form
								.closest(".mpp__main")
								.find(".woocommerce-notices-wrapper")
								.append(response.fragments.notices_html);

							gsap.set($(".woocommerce-message"), {
								y: "2rem",
							});

							gsap.to($(".woocommerce-message"), {
								y: "0rem",
								autoAlpha: 1,
								ease: "expo.out",
								duration: 0.6,
								pointerEvents: "auto",
								onComplete: function () {
									gsap.to($(".woocommerce-message"), {
										autoAlpha: 0,
										ease: "expo.out",
										duration: 0.6,
										delay: 4,
										pointerEvents: "none",
									});
								},
							});
						} else {
							$("body").addClass("quickview-open");

							lenis.stop();
						}

						// form.unblock();
						form.removeClass("--loading");
					},
				});
			});

			if (
				$(window).width() > 1024 &&
				$(".woocommerce-notices-wrapper").length > 0
			) {
				var startTrigger = ScrollTrigger.create({
					trigger: $(".mpp__main"),
					// start: '100px 0px',
					start: "67px top",
					end: "bottom 50%",
					// markers: true,
					pin: ".woocommerce-notices-wrapper",
					pinSpacing: false,
				});
			}
		}

		/**
		 * proScript
		 */
		function proScript() {
			var proSwiper = new Swiper(".pro__realisations", {
				modules: [Navigation],
				slidesPerView: 1,
				spaceBetween: 16,
				grabCursor: true,
				navigation: {
					nextEl: ".swiper-button-next",
					prevEl: ".swiper-button-prev",
				},
				breakpoints: {
					768: {
						slidesPerView: 2,
						spaceBetween: 24,
					},
				},
			});
		}

		/**
		 * miniCartHandler
		 * toggle, update mini-cart
		 */
		function miniCartHandler() {
			$(".woocommerce-mini-cart__inner").css(
				"padding-bottom",
				$(".woocommerce-mini-cart__bottom").outerHeight()
			);

			/**
			 *  Refresh mini-cart
			 */
			$(document).on("click", ".site-header__cart", function (e) {
				e.preventDefault();

				$("body").addClass("quickview-open");

				lenis.stop();

				$.ajax({
					url: woocommerce_params.ajax_url,
					type: "post",
					data: {
						action: "ajax_update_mini_cart",
					},
					success: function (response) {
						$("#mini-cart-container").html(response);
					},
				});
			});

			/**
			 *  Close mini-cart
			 */
			$(".mpp__cart-quickview .close").on("click", function () {
				$("body").removeClass("quickview-open");

				lenis.start();
			});

			$(document).mouseup(function (e) {
				var cartQuickview = $(".mpp__cart-quickview__content");

				if (
					!cartQuickview.is(e.target) &&
					cartQuickview.has(e.target).length === 0
				) {
					$("body").removeClass("quickview-open");
					lenis.start();
				}
			});
		}

		// Cart page / Increments/Decrements qty input
		$(".woocommerce-cart-form").on(
			"click",
			"button.plus, button.minus",
			function (e) {
				var qty = $(this).parent().find(".qty");
				var val = parseFloat(qty.val());
				var max = parseFloat(qty.attr("max"));
				var min = parseFloat(qty.attr("min"));
				var step = parseFloat(qty.attr("step"));
				if ($(this).is(".plus")) {
					if (max && max <= val) {
						qty.val(max).change();
					} else {
						qty.val(val + step).change();
					}
				} else {
					if (min && min >= val) {
						qty.val(min).change();
					} else if (val > 1) {
						qty.val(val - step).change();
					}
				}
			}
		);

		// Cart page / Auto-update cart on qty change
		let timeout;
		$(".woocommerce").on("change", "input.qty", function () {
			if (timeout !== undefined) {
				clearTimeout(timeout);
			}
			timeout = setTimeout(function () {
				$("[name='update_cart']").trigger("click"); // trigger cart update
			}, 1000); // 1 second delay, half a second (500) seems comfortable too
		});

		// Focus styles for menus when using keyboard navigation

		// Properly update the ARIA states on focus (keyboard) and mouse over events
		$("nav > ul").on(
			"focus.wparia  mouseenter.wparia",
			'[aria-haspopup="true"]',
			function (ev) {
				$(ev.currentTarget).attr("aria-expanded", true);
			}
		);

		// Properly update the ARIA states on blur (keyboard) and mouse out events
		$("nav > ul").on(
			"blur.wparia  mouseleave.wparia",
			'[aria-haspopup="true"]',
			function (ev) {
				$(ev.currentTarget).attr("aria-expanded", false);
			}
		);

		// Listen for class changes on body and start/stop lenis
		$.fn.onClassChange = function (cb) {
			return $(this).each((_, el) => {
				new MutationObserver((mutations) => {
					mutations.forEach(
						(mutation) => cb && cb(mutation.target, mutation.target.className)
					);
				}).observe(el, {
					attributes: true,
					attributeFilter: ["class"], // only listen for class attribute changes
				});
			});
		};

		// usage:
		$("body.post-type-archive-product").onClassChange((el) => {
			if ($(el).hasClass("awf-togglable-preset-on")) {
				lenis.stop();
			}
		});

		$(document).on("awf_after_ajax_products_update", function () {
			ScrollTrigger.refresh();
		});

		window.onload = (function () {
			$("body").addClass("loaded");

			// pageLoadedAnim() ? pageLoadedAnim() : "";

			if (document.querySelector(".awf-filters-form")) {
				document
					.querySelector(".awf-left-popup-sidebar-mode")
					.setAttribute("data-lenis-prevent", "true");

				const filtersForm = document.querySelector(".awf-filters-form"),
					filtersFormFields = filtersForm.querySelectorAll(
						".awf-filter-wrapper"
					),
					filtersResetBtnContainer = document.querySelector(
						".awf-reset-btn-container"
					),
					filtersSubmitBtnContainer =
						document.querySelector(".awf-btn-container");

				document
					.querySelector(".awf-togglable-preset-btn")
					.classList.add("btn");
				document
					.querySelector(".awf-togglable-preset-btn")
					.setAttribute("tabindex", "0");

				filtersResetBtnContainer
					.querySelector(".awf-reset-btn")
					.classList.add("btn", "btn--invert");
				filtersSubmitBtnContainer
					.querySelector(".awf-apply-filter-btn")
					.classList.add("btn", "w-100");

				var filtersFormFieldsWrapper = document.createElement("div");
				filtersFormFieldsWrapper.classList.add(
					"awf-filters-form-fields-wrapper"
				);
				filtersForm.appendChild(filtersFormFieldsWrapper);
				filtersFormFieldsWrapper.append(...filtersFormFields);

				var filtersFormFooter = document.createElement("div");
				filtersFormFooter.classList.add("awf-filters-form-footer");
				filtersForm.appendChild(filtersFormFooter);

				filtersFormFooter.insertAdjacentElement(
					"beforeend",
					filtersSubmitBtnContainer
				);
				filtersFormFooter.insertAdjacentElement(
					"afterbegin",
					filtersResetBtnContainer
				);

				// Init custom scrollbar
				SimpleScrollbar.initEl(
					document.querySelector(".awf-filters-form-fields-wrapper")
				);
			}

			setTimeout(function () {
				loaderTl.to(".loader", {
					autoAlpha: 0,
					duration: 0.75,
					ease: "expo.out",
					onComplete: () => {
						loaderTl.pause();
					},
				});

				ScrollTrigger.refresh();
			}, 150);
		})();

		// $(document).ready(function () {
		generalScripts();
		miniCartHandler();

		$(document).on("click", ".woocommerce-notices-wrapper .close", function () {
			gsap.to($(this).closest(".woocommerce-message"), {
				// y: '0rem',
				autoAlpha: 0,
				ease: "expo.out",
				duration: 0.6,
				pointerEvents: "none",
			});
		});

		// Conditional scripts
		if ($("body").hasClass("home")) {
			homeScripts();
			reviewSlider();
		}

		// Conditional scripts
		if (
			$("body").hasClass("post-type-archive-product") ||
			$("body").hasClass("tax-product_cat")
		) {
			archiveProductScripts();
		}

		if ($("body").hasClass("single-product")) {
			singleProductScripts();
			reviewSlider();
		}

		if ($(".mpp__main").hasClass("pro")) {
			proScript();
		}
		// });
	});
})(jQuery, this);
